'use client'

import Script from 'next/script'
import { FC, useEffect } from 'react'
import { BehaviorSubject, EMPTY } from 'rxjs'
import { catchError, take, tap } from 'rxjs/operators'

import { asyncGlobalVariable } from '../../../../lib/utils/asyncGlobalVariable'
import {
  NostoApi,
  NostoLoadData,
  NostoPlacementId,
  NostoRecommendation,
  NostoSession,
} from '../../../../types/nosto'
import { useConsentPolicy } from '../../../_providers/ConsentPolicyProvider.client'
import { useUser } from '../../../_providers/UserProvider.client'
import { shortenShopifyId } from '../../../_utils/shortenShopifyId'

import { reportClientError } from './DataDogRumScript'

/**
 * `globalThis.nostojs` is initialized asynchronously by scripts injected by
 * GTM and is not guaranteed to have already been set by the time we need to
 * track Nosto actions like page views. The `nostojs$` observable will emit one
 * time, as soon as `globalThis.nostojs` is defined, with the value of
 * `globalThis.nostojs`. This way consuming code can pipe/tap the observable’s
 * inner value and not have to worry about whether or not it is defined yet.
 * After 20 seconds we stop waiting for `globalThis.nostojs` to become defined
 * to avoid a memory leak.
 */
const nostojs$ = asyncGlobalVariable(() => globalThis.nostojs)

class NostoClient {
  private readonly _recommendations = new BehaviorSubject<
    Partial<Record<NostoPlacementId, NostoRecommendation>>
  >({})

  readonly recommendations$ = this._recommendations.asObservable()

  private trackingAllowed: boolean

  constructor() {
    this.trackingAllowed = false
  }

  enableTracking() {
    this.trackingAllowed = true
  }

  disableTracking() {
    this.trackingAllowed = false
  }

  /**
   * Clear some or all placement recommendations.
   *
   * @example
   * // Clears only the specified placement recommendations:
   * clearRecommendations(['frontpage-nosto-1', 'frontpage-nosto-2'])
   *
   * @example
   * // Clears all placement recommendations:
   * clearRecommendations()
   */
  clearRecommendations(placements: readonly NostoPlacementId[] = []): void {
    const nextRecommendations: Partial<Record<NostoPlacementId, NostoRecommendation>> =
      Object.fromEntries(
        Object.entries(this._recommendations.value).filter(
          ([placementId]) =>
            placements.length > 0 && !placements.includes(placementId as NostoPlacementId)
        )
      )
    this._recommendations.next(nextRecommendations)
  }

  trackCartChange(props: {
    currencyCode: string
    cartItems: Array<{
      title: string
      quantity: number
      effectivePriceInCents: number
      shopifyProductGid: string
      shopifyVariantGid: string
    }>
    nostoPlacementIds: readonly NostoPlacementId[]
  }): void {
    this.useSession(session => {
      const cartItemsToTrack = this.trackingAllowed
        ? props.cartItems.map(cartItem => {
            return {
              name: cartItem.title,
              price_currency_code: props.currencyCode,
              product_id: shortenShopifyId(cartItem.shopifyProductGid),
              quantity: cartItem.quantity,
              sku_id: shortenShopifyId(cartItem.shopifyVariantGid),
              unit_price: cartItem.effectivePriceInCents / 100,
            }
          })
        : []

      session
        .setCart({
          items: cartItemsToTrack,
        })
        .viewCart()
        .setPlacements(props.nostoPlacementIds)
        .load()
        .then(this.updateRecommendations)
    })
  }

  trackCollectionPageView(props: {
    collectionHandle: string
    nostoPlacementIds: readonly NostoPlacementId[]
  }): void {
    this.useSession(session => {
      session
        .viewCategory(props.collectionHandle)
        .setPlacements(props.nostoPlacementIds)
        .load()
        .then(this.updateRecommendations)
    })
  }

  trackCustomerChange(props: {
    user: {
      shopifyId: string
      email: string
      firstName: string
      lastName: string
    } | null
  }): void {
    const userToTrack =
      this.trackingAllowed && props.user
        ? {
            customer_reference: props.user.shopifyId,
            email: props.user.email,
            first_name: props.user.firstName,
            last_name: props.user.lastName,
            newsletter: false,
          }
        : {}

    this.useSession(session => {
      session.setCustomer(userToTrack)
    })
  }

  trackHomePageView(placements: readonly NostoPlacementId[] = []): void {
    this.useSession(session => {
      session.viewFrontPage().setPlacements(placements).load().then(this.updateRecommendations)
    })
  }

  trackGeneralPageView(placements: readonly NostoPlacementId[] = []): void {
    this.useSession(session => {
      session.viewOther().setPlacements(placements).load().then(this.updateRecommendations)
    })
  }

  trackNotFoundPageView(placements: readonly NostoPlacementId[] = []): void {
    this.useSession(session => {
      session.viewNotFound().setPlacements(placements).load().then(this.updateRecommendations)
    })
  }

  trackProductPageView(props: {
    shopifyProductGid: string
    nostoPlacementIds: readonly NostoPlacementId[]
  }): void {
    this.useSession(session => {
      session
        .viewProduct(shortenShopifyId(props.shopifyProductGid))
        .setPlacements(props.nostoPlacementIds)
        .load()
        .then(this.updateRecommendations)
    })
  }

  trackSearchEvent(searchText: string, placements: readonly NostoPlacementId[] = []): void {
    this.useSession(session => {
      const searchPhraseToTrack = this.trackingAllowed ? searchText : ''
      session
        .viewSearch(searchPhraseToTrack)
        .setPlacements(placements)
        .load()
        .then(this.updateRecommendations)
    })
  }

  recordProductClick(shopifyProductGid: string, resultId: string): void {
    globalThis.nostojs((api: NostoApi) => {
      api
        .recordAttribution({
          type: 'vp',
          target: shortenShopifyId(shopifyProductGid),
          ref: resultId,
        })
        .done()
    })
  }

  private useSession(action: (session: NostoSession) => void): void {
    nostojs$
      .pipe(
        tap(nostojs => nostojs((api: NostoApi) => action(api.defaultSession()))),
        catchError(error => {
          reportClientError({
            error,
            context: {
              scope: 'Nosto',
              label: 'Nosto tracking failure',
            },
          })
          return EMPTY
        }),
        take(1)
      )
      .subscribe()
  }

  private updateRecommendations = (data: NostoLoadData): void => {
    this._recommendations.next({
      ...this._recommendations.value,
      ...data.recommendations,
    })
  }
}

const globalNostoClient = new NostoClient()

export const useNostoClient = () => {
  const {
    consentPolicy: { functional },
  } = useConsentPolicy()

  if (functional === 'allowed') {
    globalNostoClient.enableTracking()
  } else {
    globalNostoClient.disableTracking()
  }

  return globalNostoClient
}

const nostoId = process.env.NEXT_PUBLIC_NOSTO_ID

// Hoisted variables are shared between SSRs
// This variable should only be referenced on the client
// so it should only be used within a `useEffect`
const trackNostoCustomerSession: {
  customer: {
    email: string
  } | null
} = {
  customer: null,
}

export const NostoScript: FC = () => {
  const {
    consentPolicy: { essential },
  } = useConsentPolicy()
  const { user, userStatus } = useUser()
  const nostoClient = useNostoClient()

  useEffect(() => {
    if (Boolean(globalThis.nostojs)) {
      globalThis.nostojs((api: NostoApi) => {
        api.setAutoLoad(false)
      })
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalThis.nostojs])

  useEffect(() => {
    if (userStatus !== 'resolved') return

    // If user has changed, track accordingly
    if (trackNostoCustomerSession.customer?.email !== user?.email) {
      nostoClient.trackCustomerChange({
        user:
          user && user.shopifyId
            ? {
                shopifyId: user.shopifyId,
                email: user.email,
                firstName: user.profile.firstName,
                lastName: user.profile.lastName ?? '',
              }
            : null,
      })
    }

    // Update tracked user info
    trackNostoCustomerSession.customer = user ? { email: user.email } : null
  }, [user, nostoClient, userStatus])

  if (!nostoId || essential !== 'allowed') return null

  return (
    <Script
      id='nosto-script'
      strategy='afterInteractive' // Scripts that require cookie consent cannot be before interactive
      src={`//connect.nosto.com/include/${nostoId}`}
    />
  )
}
